import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { handleChatClientCreated } from 'src/actions/chats/clients';
import { handleChatEvent, handleChatIncomingMessage, handleChatOutgoingMessage } from 'src/actions/chats/messages';
import { fetchUnreadMessagesForAllChannels } from 'src/actions/chats/unreadMessages';
import { SupportedChatChannel } from 'src/chats/supportedChatChannel';
import chatWebsocketManager from 'src/chats/websocket/chatWebsocketManager';
import { AppState } from 'src/reducers';
import { selectUnreadMessagesCountByChannel } from 'src/reducers/chats/messages';
import { Channel, setChannel } from 'src/reducers/slices/channels';
import { Routes } from 'src/routes/newRoutes';
import { ChatClient } from 'src/types/chatClient';
import { ChatEvent } from 'src/types/chatEvent';
import { ChatMessage } from 'src/types/chatMessage';
import { MenuEntry } from 'src/types/menu';

const useWebsocketConnection = () => {
  const userData = useSelector((state: AppState) => state.login.userData);
  const dispatch = useDispatch();

  const onIncomingMessage = useCallback(
    (message: ChatMessage) => dispatch(handleChatIncomingMessage(message)),
    [dispatch]
  );
  const onOutgoingMessage = useCallback(
    (message: ChatMessage) => dispatch(handleChatOutgoingMessage(message)),
    [dispatch]
  );
  const onEvent = useCallback((event: ChatEvent) => dispatch(handleChatEvent(event)), [dispatch]);
  const onClientCreated = useCallback((client: ChatClient) => dispatch(handleChatClientCreated(client)), [dispatch]);

  useEffect(() => {
    chatWebsocketManager.registerListeners({
      onIncomingMessage,
      onOutgoingMessage,
      onEvent,
      onClientCreated,
      onConnect: () => dispatch(fetchUnreadMessagesForAllChannels()),
    });

    chatWebsocketManager.openConnection({
      customerId: userData.customerId!,
      customerUuid: userData.customerUuid!,
    });

    return () => {
      chatWebsocketManager.unregisterListeners();
      chatWebsocketManager.closeConnection();
    };
  }, [dispatch, userData.customerId, userData.customerUuid, onIncomingMessage, onOutgoingMessage, onEvent, onClientCreated]);
};

const useUnreadMessages = () => {
  const dispatch = useDispatch();
  const unreadSmsMessages = useSelector((state: AppState) =>
    selectUnreadMessagesCountByChannel(state, SupportedChatChannel.SMS)
  );
  const unreadWhatsappMessages = useSelector((state: AppState) =>
    selectUnreadMessagesCountByChannel(state, SupportedChatChannel.WHATSAPP)
  );
  const unreadViberMessages = useSelector((state: AppState) =>
    selectUnreadMessagesCountByChannel(state, SupportedChatChannel.VIBER)
  );

  useEffect(() => {
    dispatch(fetchUnreadMessagesForAllChannels());
  }, [dispatch]);

  return unreadSmsMessages + unreadWhatsappMessages + unreadViberMessages;
};

const useFooterLinksData = () => {
  const intl = useIntl();

  return [
    {
      label: intl.formatMessage({ id: 'dashboard.terms.conditions' }),
      href: '#terms-conditions',
    },
    {
      label: intl.formatMessage({ id: 'dashboard.data.processing' }),
      href: '#data-processing',
    },
  ];
};

const useMenuData = () => {
  const unreadMessages = useUnreadMessages();
  const selectedChannel = useSelector((state: AppState) => state.channels.channel);
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const formatMenuLabel = (id: string): string => intl.formatMessage({ id, defaultMessage: id });

  // Legacy approach to render menu items got from BE is commented.
  // In case we will face with any issues with menu items, we can uncomment this code and use it.
  const menuData = useSelector((state: AppState) => state.app.menu);
  const filteredAdminMenuData = menuData.filter((item: MenuEntry) => item.label !== 'nav.account');

  const userAuthorizations = useSelector(
    (state: AppState) => state.login.userData.authorizations
  );
  const isUser = userAuthorizations?.USER;

  const getIcon = (icon: string | undefined) => {
    if (!icon) {
      return 'inbox';
    }

    if (icon === 'chat') {
      return 'inbox';
    }

    if (icon === 'reports') {
      return 'analytics';
    }

    if (icon === 'admin') {
      return 'admin';
    }

    if (icon === 'mnp') {
      return 'mnp';
    }

    return icon;
  };

  const adminMenu = filteredAdminMenuData.map((item) => {
    // Special handling for nav.campaign item
    if (item.label === 'nav.campaign') {
      return {
        id: item.label,
        icon: getIcon(item?.icon),
        label: formatMenuLabel(item.label),
        isActive: '/smsCampaign/history' === history.location.pathname,
        onPress: () => {
          history.push('/smsCampaign/history');
        },
        subItems: [], // No submenus for this item
      };
    }

    // Special handling for nav.contacts item
    if (item.label === 'nav.contacts') {
      return {
        id: item.label,
        icon: getIcon(item?.icon),
        label: 'Audience',
        isActive: '/contacts/manage' === history.location.pathname,
        onPress: () => {
          history.push('/contacts/manage');
        },
        subItems: [
          {
            id: 'Contacts',
            label: 'Contacts',
            isActive: '/contacts/manage' === history.location.pathname,
            onPress: () => {
              history.push('/contacts/manage');
            },
          },
        ],
      };
    }

    // Special handling for nav.smschat item
    if (item.label === 'nav.smschat') {
      return {
        id: item.label,
        icon: getIcon(item?.icon),
        badge: unreadMessages,
        label: intl.formatMessage({ id: 'nav.inbox' }),
        isActive: item.link === history.location.pathname,
        onPress: () => {
          history.push(item.link);
        },
        subItems: [],
      };
    }

    // Special handling for "nav.channels.user" item
    if (item.label === 'nav.channels.user') {
      return {
        id: item.label,
        icon: getIcon(item?.icon),
        label: formatMenuLabel(item.label),
        isActive:
          history.location.pathname === Routes.CHANNELS || history.location.pathname === Routes.CHANNELS_VIBER_CONNECT,
        onPress: () => {
          history.push(Routes.CHANNELS);
        },
        subItems: [],
      };
    }

    // Default handling for other items
    return {
      id: item.label,
      icon: getIcon(item?.icon),
      label: formatMenuLabel(item.label),
      isActive:
        item.link === history.location.pathname ||
        (item.link === Routes.DASHBOARD && history.location.pathname === Routes.ROOT),
      onPress: () => {
        if (item.link === Routes.DASHBOARD) {
          history.push(Routes.ROOT);
        } else {
          history.push(item.link);
        }
      },
      subItems: item.subMenu.map((subItem) => {
        return {
          id: subItem.label,
          label: formatMenuLabel(subItem.label),
          isActive: subItem.link === history.location.pathname,
          onPress: () => {
            history.push(subItem.link);
          },
        };
      }),
    };
  });

  const userStaticMenu = [
    {
      id: 'nav.dashboard',
      icon: 'dashboard',
      label: intl.formatMessage({ id: 'nav.dashboard' }),
      subItems: [],
      isActive: history.location.pathname === Routes.ROOT,
      onPress: () => {
        history.push(Routes.ROOT);
      },
    },
    {
      id: 'nav.campaign',
      icon: 'sms',
      label: intl.formatMessage({ id: 'nav.campaign' }),
      isActive: Routes.CAMPAIGN_HISTORY === history.location.pathname,
      onPress: () => {
        history.push(Routes.CAMPAIGN_HISTORY);
      },
      subItems: [],
    },
    {
      id: 'nav.inbox',
      icon: 'inbox',
      badge: unreadMessages,
      label: intl.formatMessage({ id: 'nav.inbox' }),
      isActive: Routes.SMS_CHAT === history.location.pathname,
      onPress: () => {
        history.push(Routes.SMS_CHAT);
      },
      subItems: [],
    },
    {
      id: 'nav.channels.user',
      icon: 'channels',
      label: intl.formatMessage({ id: 'nav.channels.user' }),
      isActive:
        history.location.pathname === Routes.CHANNELS || history.location.pathname === Routes.CHANNELS_VIBER_CONNECT,
      onPress: () => {
        dispatch(setChannel(Channel.whatsapp));
        history.push(Routes.CHANNELS);
      },
      subItems: [
        {
          id: 'nav.templates.whatsapp',
          label: intl.formatMessage({ id: 'nav.templates.whatsapp' }),
          isActive: history.location.pathname === Routes.CHANNELS && selectedChannel === Channel.whatsapp,
          onPress: () => {
            dispatch(setChannel(Channel.whatsapp));
            history.push(Routes.CHANNELS);
          },
        },
        {
          id: 'nav.templates.viber',
          label: intl.formatMessage({ id: 'nav.templates.viber' }),
          isActive:
            (history.location.pathname === Routes.CHANNELS && selectedChannel === Channel.viber) ||
            history.location.pathname === Routes.CHANNELS_VIBER_CONNECT,
          onPress: () => {
            dispatch(setChannel(Channel.viber));
            history.push(Routes.CHANNELS);
          },
        },
      ],
    },
    {
      id: 'nav.audience',
      icon: 'contacts',
      label: intl.formatMessage({ id: 'nav.audience' }),
      onPress: () => {
        history.push(Routes.CONTACTS_MANAGE);
      },
      subItems: [
        {
          id: 'nav.contacts',
          label: intl.formatMessage({ id: 'nav.contacts' }),
          isActive: Routes.CONTACTS_MANAGE === history.location.pathname,
          onPress: () => {
            history.push(Routes.CONTACTS_MANAGE);
          },
        },
      ],
    },
    {
      id: 'nav.analytics',
      icon: 'analytics',
      label: intl.formatMessage({ id: 'nav.analytics' }),
      isActive: false,
      onPress: () => {},
      subItems: [
        {
          id: 'nav.account.overview',
          label: intl.formatMessage({ id: 'nav.account.overview' }),
          isActive: '/finance/balance' === history.location.pathname,
          onPress: () => {
            history.push('/finance/balance');
          },
        },
        {
          id: 'nav.account.cdrs',
          label: intl.formatMessage({ id: 'nav.account.cdrs' }),
          isActive: '/finance/cdrs' === history.location.pathname,
          onPress: () => {
            history.push('/finance/cdrs');
          },
        },
      ],
    },
    {
      id: 'nav.finance.user',
      icon: 'finance',
      label: intl.formatMessage({ id: 'nav.finance.user' }),
      isActive: false,
      onPress: () => {},
      subItems: [
        {
          id: 'nav.account.pricing',
          label: intl.formatMessage({ id: 'nav.account.pricing' }),
          isActive: Routes.PRICING === history.location.pathname,
          onPress: () => {
            history.push(Routes.PRICING);
          },
        },
        // {
        //   id: 'nav.user.invoices',
        //   label: intl.formatMessage({ id: 'nav.user.invoices' }),
        //   isActive: Routes.INVOICES === history.location.pathname,
        //   onPress: () => {
        //     history.push(Routes.INVOICES);
        //   },
        // },
      ],
    },
  ];

  const menuItemsByRole = isUser
    ? userStaticMenu
    : adminMenu;

  return menuItemsByRole;
};

export const useAuthenticatedLayout = () => {
  useWebsocketConnection();
  const footerLinksData = useFooterLinksData();
  const menuData = useMenuData();

  return {
    footerLinksData,
    menuData,
  };
};
